import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type DetailsHTMLAttributes,
  type DialogHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Link, type LinkProps } from "react-router-dom";
import { Icon } from "./Icon";
import "./customer-controls.css";
import "../features/marketing/public-control-alignment.css";

export type CustomerActionTone = "primary" | "creative" | "secondary" | "quiet" | "danger";

type ActionShared = {
  tone?: CustomerActionTone;
  size?: "standard" | "large";
  busy?: boolean;
  icon?: ReactNode;
  iconAfter?: ReactNode;
};

export const CustomerButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> &
    ActionShared & {
      /**
       * Opt-in, for a control that is only briefly unavailable while it keeps keyboard focus (a
       * page turning): it stays focusable, reads as unavailable and does nothing when activated.
       * A natively disabled or busy button is unaffected.
       */
      focusableDisabled?: boolean;
    }
>(function CustomerButton(
  {
    tone = "primary",
    size = "standard",
    busy = false,
    focusableDisabled = false,
    icon,
    iconAfter,
    children,
    className = "",
    disabled,
    onClick,
    role: callerRole,
    "aria-label": callerAriaLabel,
    "aria-labelledby": callerAriaLabelledBy,
    "aria-disabled": callerAriaDisabled,
    "aria-busy": callerAriaBusy,
    ...props
  },
  ref,
) {
  void callerRole;
  void callerAriaLabel;
  void callerAriaLabelledBy;
  void callerAriaDisabled;
  void callerAriaBusy;
  const held = focusableDisabled && !disabled && !busy;
  return (
    <button
      {...props}
      ref={ref}
      className={`lc-action lc-action--${tone} lc-action--${size} ${className}`.trim()}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-disabled={held || undefined}
      // Held: a click, Enter or Space does nothing, and a submit button doesn't submit its form.
      onClick={held ? (event) => event.preventDefault() : onClick}
    >
      {busy ? <span className="lc-action__spinner" aria-hidden="true" /> : icon}
      <span>{children}</span>
      {iconAfter}
    </button>
  );
});

export function CustomerLinkButton({
  tone = "primary",
  size = "standard",
  icon,
  iconAfter,
  children,
  className = "",
  ...props
}: LinkProps & Omit<ActionShared, "busy">) {
  return (
    <Link
      className={`lc-action lc-action--${tone} lc-action--${size} ${className}`.trim()}
      {...props}
    >
      {icon}
      <span>{children}</span>
      {iconAfter}
    </Link>
  );
}

export function CustomerIconButton({
  label,
  selected,
  busy,
  children,
  className = "",
  disabled,
  role: callerRole,
  "aria-label": callerAriaLabel,
  "aria-labelledby": callerAriaLabelledBy,
  "aria-disabled": callerAriaDisabled,
  "aria-busy": callerAriaBusy,
  "aria-pressed": callerAriaPressed,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  selected?: boolean;
  busy?: boolean;
}) {
  void callerRole;
  void callerAriaLabel;
  void callerAriaLabelledBy;
  void callerAriaDisabled;
  void callerAriaBusy;
  void callerAriaPressed;
  return (
    <button
      {...props}
      className={`lc-icon-action ${className}`.trim()}
      type="button"
      aria-label={label}
      aria-pressed={selected}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      title={label}
    >
      {busy ? <span className="lc-action__spinner" aria-hidden="true" /> : children}
    </button>
  );
}

/**
 * Keeps related actions together while preserving the visual hierarchy of each
 * button. Supply `label` only when the actions form a meaningful semantic
 * group; presentational wrapping otherwise stays out of the accessibility tree.
 */
export function CustomerButtonGroup({
  label,
  children,
  className = "",
  role: callerRole,
  "aria-label": callerAriaLabel,
  "aria-labelledby": callerAriaLabelledBy,
  "aria-disabled": callerAriaDisabled,
  "aria-busy": callerAriaBusy,
  ...props
}: HTMLAttributes<HTMLDivElement> & { label?: string }) {
  void callerRole;
  void callerAriaLabel;
  void callerAriaLabelledBy;
  void callerAriaDisabled;
  void callerAriaBusy;
  return (
    <div
      {...props}
      className={`lc-button-group ${className}`.trim()}
      role={label ? "group" : undefined}
      aria-label={label}
    >
      {children}
    </div>
  );
}

type FieldFrameProps = {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  description?: ReactNode;
  message?: ReactNode;
  tone?: "error" | "success" | "warning";
  count?: ReactNode;
  children: ReactNode;
};

function FieldFrame({
  id,
  label,
  required,
  optional,
  description,
  message,
  tone,
  count,
  children,
}: FieldFrameProps) {
  const hintId = description ? `${id}-description` : undefined;
  const messageId = message ? `${id}-message` : undefined;
  return (
    <div className={`lc-field${tone ? ` lc-field--${tone}` : ""}`}>
      <div className="lc-field__label-row">
        <label htmlFor={id}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
          {required && <span className="sr-only"> (required)</span>}
          {optional && <span className="lc-field__optional"> Optional</span>}
        </label>
        {count && <span className="lc-field__count">{count}</span>}
      </div>
      {description && (
        <p className="lc-field__description" id={hintId}>
          {description}
        </p>
      )}
      {children}
      {message && (
        <p
          className="lc-field__message"
          id={messageId}
          role={tone === "error" ? "alert" : "status"}
        >
          {tone === "success" && <Icon name="circleCheck" size={16} />}
          {tone === "warning" && <Icon name="alert" size={16} />}
          {tone === "error" && <Icon name="alert" size={16} />}
          <span>{message}</span>
        </p>
      )}
    </div>
  );
}

type CommonFieldProps = {
  label: string;
  description?: ReactNode;
  message?: ReactNode;
  tone?: "error" | "success" | "warning";
  optional?: boolean;
};

export const CustomerTextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & CommonFieldProps
>(function CustomerTextInput(
  { id: suppliedId, label, description, message, tone, optional, className = "", ...props },
  ref,
) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const describedBy =
    [description && `${id}-description`, message && `${id}-message`].filter(Boolean).join(" ") ||
    undefined;
  return (
    <FieldFrame
      id={id}
      label={label}
      description={description}
      message={message}
      tone={tone}
      optional={optional}
      required={props.required}
    >
      <input
        ref={ref}
        id={id}
        className={`lc-input ${className}`.trim()}
        aria-describedby={describedBy}
        aria-invalid={tone === "error" || undefined}
        {...props}
      />
    </FieldFrame>
  );
});

export const CustomerTextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & CommonFieldProps & { maxCount?: number }
>(function CustomerTextArea(
  {
    id: suppliedId,
    label,
    description,
    message,
    tone,
    optional,
    maxCount,
    value,
    defaultValue,
    className = "",
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const length = typeof value === "string" ? value.length : String(defaultValue ?? "").length;
  const describedBy =
    [description && `${id}-description`, message && `${id}-message`].filter(Boolean).join(" ") ||
    undefined;
  return (
    <FieldFrame
      id={id}
      label={label}
      description={description}
      message={message}
      tone={tone}
      optional={optional}
      required={props.required}
      count={maxCount ? `${length}/${maxCount}` : undefined}
    >
      <textarea
        ref={ref}
        id={id}
        className={`lc-input lc-textarea ${className}`.trim()}
        aria-describedby={describedBy}
        aria-invalid={tone === "error" || undefined}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxCount ?? props.maxLength}
        {...props}
      />
    </FieldFrame>
  );
});

export const CustomerSelect = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & CommonFieldProps
>(function CustomerSelect(
  {
    id: suppliedId,
    label,
    description,
    message,
    tone,
    optional,
    children,
    className = "",
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const describedBy =
    [description && `${id}-description`, message && `${id}-message`].filter(Boolean).join(" ") ||
    undefined;
  return (
    <FieldFrame
      id={id}
      label={label}
      description={description}
      message={message}
      tone={tone}
      optional={optional}
      required={props.required}
    >
      <span className="lc-select-wrap">
        <select
          ref={ref}
          id={id}
          className={`lc-input lc-select ${className}`.trim()}
          aria-describedby={describedBy}
          aria-invalid={tone === "error" || undefined}
          {...props}
        >
          {children}
        </select>
        <Icon name="chevron" size={18} />
      </span>
    </FieldFrame>
  );
});

export function CustomerCheckbox({
  label,
  description,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className={`lc-check ${className}`.trim()} htmlFor={id}>
      <input id={id} type="checkbox" {...props} />
      <span className="lc-check__mark" aria-hidden="true">
        <Icon name="check" size={15} />
      </span>
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

export function CustomerRadio({
  label,
  description,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className={`lc-check lc-radio ${className}`.trim()} htmlFor={id}>
      <input id={id} type="radio" {...props} />
      <span className="lc-check__mark" aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

export function CustomerSwitch({
  label,
  description,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className={`lc-switch ${className}`.trim()} htmlFor={id}>
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <input id={id} type="checkbox" role="switch" {...props} />
      <span className="lc-switch__track" aria-hidden="true">
        <i />
      </span>
    </label>
  );
}

export function CustomerSegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; icon?: ReactNode }>;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="lc-segmented" disabled={disabled}>
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label key={option.value}>
          <input
            type="radio"
            name={label}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>
            {option.icon}
            {option.label}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

export function CustomerChoiceCard({
  title,
  description,
  selected,
  children,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  title: string;
  description: string;
  selected?: boolean;
  children?: ReactNode;
}) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className={`lc-choice-card${selected ? " is-selected" : ""}`} htmlFor={id}>
      <input id={id} type="radio" checked={selected} {...props} />
      <span className="lc-choice-card__copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      {children}
      <span className="lc-choice-card__radio" aria-hidden="true" />
    </label>
  );
}

export const CustomerInlineStatus = forwardRef<
  HTMLDivElement,
  {
    tone?: "info" | "success" | "warning" | "error";
    title?: string;
    children: ReactNode;
    action?: ReactNode;
    className?: string;
    /** -1 lets a caller move focus onto the message after an action so it is announced. */
    tabIndex?: number;
  }
>(function CustomerInlineStatus(
  { tone = "info", title, children, action, className = "", tabIndex },
  ref,
) {
  const icon = tone === "success" ? "circleCheck" : tone === "info" ? "info" : "alert";
  return (
    <div
      ref={ref}
      className={`lc-status lc-status--${tone} ${className}`.trim()}
      role={tone === "error" ? "alert" : "status"}
      tabIndex={tabIndex}
    >
      <Icon name={icon} size={20} />
      <div>
        {title && <strong>{title}</strong>}
        <div>{children}</div>
      </div>
      {action && <div className="lc-status__action">{action}</div>}
    </div>
  );
});

export function CustomerFileUpload({
  label,
  description,
  accept,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  accept?: string;
  onChange?: InputHTMLAttributes<HTMLInputElement>["onChange"];
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label className="lc-upload" htmlFor={id}>
      <input id={id} type="file" accept={accept} onChange={onChange} disabled={disabled} />
      <Icon name="upload" size={26} />
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
    </label>
  );
}

export type VoiceRecorderState =
  | "idle"
  | "requesting"
  | "recording"
  | "processing"
  | "ready"
  | "error";

export function CustomerVoiceRecorder({
  state,
  duration = "0:00",
  onPrimaryAction,
  onRetry,
}: {
  state: VoiceRecorderState;
  duration?: string;
  onPrimaryAction?: () => void;
  onRetry?: () => void;
}) {
  const labels: Record<VoiceRecorderState, string> = {
    idle: "Record a voice memory",
    requesting: "Allow microphone access",
    recording: "Stop recording",
    processing: "Preparing your transcript",
    ready: "Play voice memory",
    error: "Recording unavailable",
  };
  const icon = state === "recording" ? "stop" : state === "ready" ? "play" : "mic";
  return (
    <div className={`lc-voice lc-voice--${state}`}>
      <CustomerIconButton
        label={labels[state]}
        busy={state === "processing"}
        onClick={onPrimaryAction}
        disabled={state === "error"}
      >
        <Icon name={icon} size={22} />
      </CustomerIconButton>
      <span className="lc-voice__wave" aria-hidden="true">
        {Array.from({ length: 18 }, (_, index) => (
          <i key={index} />
        ))}
      </span>
      <span className="lc-voice__time">{duration}</span>
      {state === "error" && onRetry && (
        <button type="button" className="lc-text-action" onClick={onRetry}>
          Try again
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {labels[state]}
      </span>
    </div>
  );
}

export function CustomerProgressSteps({
  label,
  steps,
  current,
}: {
  label: string;
  steps: Array<{ title: string; detail?: string }>;
  current: number;
}) {
  return (
    <ol
      className="lc-progress-steps"
      aria-label={label}
      style={{ "--steps": steps.length } as CSSProperties}
    >
      {steps.map((step, index) => (
        <li
          key={step.title}
          className={index === current ? "is-current" : index < current ? "is-complete" : undefined}
          aria-current={index === current ? "step" : undefined}
        >
          <span aria-hidden="true">
            {index < current ? <Icon name="check" size={16} /> : index + 1}
          </span>
          <div>
            <strong>{step.title}</strong>
            {step.detail && <small>{step.detail}</small>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function CustomerLabsDisclosure({
  title = "Explore Labs",
  description = "Experimental tools kept separate from your book-making essentials.",
  children,
  ...props
}: DetailsHTMLAttributes<HTMLDetailsElement> & {
  title?: string;
  description?: string;
}) {
  return (
    <details className="lc-labs" {...props}>
      <summary>
        <Icon name="flask" size={22} />
        <span>
          <strong>{title}</strong>
          <small>{description}</small>
        </span>
        <Icon name="chevron" size={18} />
      </summary>
      <div className="lc-labs__body">{children}</div>
    </details>
  );
}

export type CustomerDialogVariant = "dialog" | "bottom-sheet" | "full-height-sheet";

type CustomerDialogProps = Omit<
  DialogHTMLAttributes<HTMLDialogElement>,
  | "open"
  | "onClose"
  | "onCancel"
  | "role"
  | "aria-label"
  | "aria-labelledby"
  | "aria-describedby"
  | "aria-modal"
> & {
  open: boolean;
  title: string;
  description?: string;
  role?: "dialog" | "alertdialog";
  onClose: () => void;
  variant?: CustomerDialogVariant;
  initialFocusRef?: RefObject<HTMLElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
  lockBodyScroll?: boolean;
};

type BodyScrollSnapshot = {
  bodyOverflow: string;
  bodyPaddingInlineEnd: string;
  rootOverflow: string;
};

let bodyScrollLockCount = 0;
let bodyScrollSnapshot: BodyScrollSnapshot | null = null;

function acquireBodyScrollLock() {
  if (typeof document === "undefined" || typeof window === "undefined") return () => undefined;

  const body = document.body;
  const root = document.documentElement;
  if (bodyScrollLockCount === 0) {
    bodyScrollSnapshot = {
      bodyOverflow: body.style.overflow,
      bodyPaddingInlineEnd: body.style.paddingInlineEnd,
      rootOverflow: root.style.overflow,
    };
    const scrollbarWidth =
      root.clientWidth > 0 ? Math.max(0, window.innerWidth - root.clientWidth) : 0;
    if (scrollbarWidth > 0) {
      const currentPadding = Number.parseFloat(window.getComputedStyle(body).paddingInlineEnd) || 0;
      body.style.paddingInlineEnd = `${currentPadding + scrollbarWidth}px`;
    }
    body.style.overflow = "hidden";
    root.style.overflow = "hidden";
  }
  bodyScrollLockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount !== 0 || !bodyScrollSnapshot) return;
    body.style.overflow = bodyScrollSnapshot.bodyOverflow;
    body.style.paddingInlineEnd = bodyScrollSnapshot.bodyPaddingInlineEnd;
    root.style.overflow = bodyScrollSnapshot.rootOverflow;
    bodyScrollSnapshot = null;
  };
}

function canReceiveRestoredFocus(target: HTMLElement | null): target is HTMLElement {
  return Boolean(
    target?.isConnected &&
      !target.hasAttribute("disabled") &&
      target.getAttribute("aria-disabled") !== "true" &&
      !target.closest("[inert]"),
  );
}

function focusWithoutScrolling(target: HTMLElement) {
  try {
    target.focus({ preventScroll: true });
  } catch {
    target.focus();
  }
}

const dialogFocusableSelector = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function dialogFocusableElements(dialog: HTMLDialogElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(dialogFocusableSelector)).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute("aria-hidden") !== "true" &&
      !element.closest("[hidden], [aria-hidden='true'], [inert]") &&
      getComputedStyle(element).display !== "none" &&
      getComputedStyle(element).visibility !== "hidden",
  );
}

/**
 * A controlled native dialog. `showModal()` owns browser focus containment,
 * background inertness, and Escape semantics; this wrapper only coordinates
 * initial/return focus and shared scroll locking around that native lifecycle.
 */
export function CustomerDialog({
  open,
  title,
  description,
  role = "dialog",
  onClose,
  variant = "dialog",
  initialFocusRef,
  returnFocusRef,
  lockBodyScroll = true,
  children,
  className = "",
  ...props
}: CustomerDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const capturedReturnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const controlledCloseRef = useRef(false);
  const currentInitialFocusRef = useRef(initialFocusRef);
  const currentReturnFocusRef = useRef(returnFocusRef);
  const titleId = useId();
  const descriptionId = useId();

  currentInitialFocusRef.current = initialFocusRef;
  currentReturnFocusRef.current = returnFocusRef;

  const restoreFocus = useCallback(() => {
    const explicitTarget = currentReturnFocusRef.current?.current ?? null;
    const target = canReceiveRestoredFocus(explicitTarget)
      ? explicitTarget
      : canReceiveRestoredFocus(capturedReturnFocusRef.current)
        ? capturedReturnFocusRef.current
        : null;
    capturedReturnFocusRef.current = null;
    if (target) focusWithoutScrolling(target);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      if (!wasOpenRef.current) {
        const activeElement = document.activeElement;
        capturedReturnFocusRef.current =
          activeElement instanceof HTMLElement && activeElement !== document.body
            ? activeElement
            : null;
      }
      controlledCloseRef.current = false;
      if (!dialog.open) {
        if (typeof dialog.showModal === "function") dialog.showModal();
        else dialog.setAttribute("open", "");
      }
      wasOpenRef.current = true;
      const initialTarget = currentInitialFocusRef.current?.current ?? null;
      if (initialTarget && dialog.contains(initialTarget) && canReceiveRestoredFocus(initialTarget))
        focusWithoutScrolling(initialTarget);
      return;
    }

    if (wasOpenRef.current) {
      controlledCloseRef.current = true;
      wasOpenRef.current = false;
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
      restoreFocus();
    }
  }, [open, restoreFocus]);

  useEffect(() => {
    if (!open || !lockBodyScroll) return;
    return acquireBodyScrollLock();
  }, [lockBodyScroll, open]);

  useEffect(
    () => () => {
      if (!wasOpenRef.current) return;
      controlledCloseRef.current = true;
      wasOpenRef.current = false;
      restoreFocus();
    },
    [restoreFocus],
  );

  return (
    <dialog
      {...props}
      ref={dialogRef}
      role={role}
      className={`lc-dialog lc-dialog--${variant} ${className}`.trim()}
      data-variant={variant}
      aria-modal={open ? "true" : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={(event) => {
        props.onKeyDown?.(event);
        if (event.defaultPrevented || event.key !== "Tab") return;
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = dialogFocusableElements(dialog);
        const first = focusable.at(0);
        const last = focusable.at(-1);
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          focusWithoutScrolling(last);
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          focusWithoutScrolling(first);
        }
      }}
      onClose={() => {
        if (controlledCloseRef.current || !wasOpenRef.current) return;
        wasOpenRef.current = false;
        restoreFocus();
        onClose();
      }}
    >
      <header>
        <div>
          <h2 id={titleId}>{title}</h2>
          {description && <p id={descriptionId}>{description}</p>}
        </div>
        <CustomerIconButton label="Close" onClick={onClose}>
          <Icon name="close" />
        </CustomerIconButton>
      </header>
      <div className="lc-dialog__body">{children}</div>
    </dialog>
  );
}

export function CustomerStickyNextAction({
  context,
  action,
}: {
  context: ReactNode;
  action: ReactNode;
}) {
  return (
    <aside className="lc-sticky-action" aria-label="Next action">
      <div>{context}</div>
      {action}
    </aside>
  );
}
